import Modal from "react-bootstrap/Modal";
import React, { useContext, useMemo, useState } from "react";
import _ from "lodash";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ExpertContext } from "../../data/expert_context_management";
import useExpertProfileNames from "../../data/use_expert_profile_names";
import useExpertUpdateApi from "../../data/use_expert_update_api";
import SaveSpinnerButton from "../../../profiles/SaveSpinnerButton";
import ServerErrorAlert from "../../../profiles/ServerErrorAlert";
import VisibilityControl from "../../../profiles/VisibilityControl";
import LocationField from "../../../profiles/LocationField";
import ExpertSchema from "../../../../yup_schemas/expert_schema";

const HEADER_ERROR_LABELS = {
  first_name: "First Name",
  last_name: "Last Name",
  name: "Name",
  headline_custom: "Headline",
  description: "About",
  location_string: "Location",
  visibility: "Visibility",
};

/**
 * Canonicalizes a free-text field to the only two shapes the server should ever see: a trimmed nonblank
 * string, or null.
 *
 * The displayed headline is a coalesce over headline_custom and the featured job title, so an empty
 * string would mask the fallback and render a blank headline — clearing the field has to send null, not
 * "". Applying this to BOTH sides of the changed-fields diff is also what keeps a text input on an
 * expert that has no custom headline (raw null, rendered as an empty box) from looking like a change and
 * blanking the headline on every unrelated save. The same holds for the About text, which is null on a
 * sparse profile.
 */
function canonicalizeText(value) {
  const trimmed = typeof value === "string" ? value.trim() : value;
  return trimmed || null;
}

/**
 * Compares two objects and returns a new object with only the properties that
 * have changed.
 *
 * @param {Object} originalData - The original data object.
 * @param {Object} newData - The updated data object.
 * @return {Object} An object containing only the properties that have changed.
 *
 * @example
 * getChangedData({ a: 1, b: 3 }, { a: 1, b: 2 })
 *   => { b: 2 }
 * getChangedData({ a: 'foo', b: 'baz' }, { a: 'foo', c: 'bar' })
 *   => { c: 'bar' }
 * getChangedData({ x: 10, y: 20 }, { x: 10, y: 20 })
 *   => {}
 */
function getChangedData(originalData, newData) {
  return _.reduce(
    newData,
    (result, value, key) => (!_.isEqual(value, originalData[key]) ? { ...result, [key]: value } : result),
    {},
  );
}

// Expert header edit body. Reads the already-loaded expert from ExpertContext and writes through
// use_expert_update_api; the entity fields (About, Headline) are the same in every case, and the name
// field is what differs.
//
// Who may rename an expert, and where the name lives, is not one question but two. An expert who
// represents themselves is the same person as a promoter profile, and their name is only editable on
// that profile — the server rejects renaming a self-expert through the entity endpoint outright. An
// expert with no such profile (a client an agency promotes) has no person record behind it, so its name
// is an ordinary entity attribute. And holding authority over the entity does not imply holding
// authority over the person: a second agency co-representing an expert may edit the entity while the
// name stays the expert's own. Hence three forms:
//
//   self-representing + profile authority -> first/last name (on the profile) + the entity fields
//   self-representing, no profile authority -> the entity fields only
//   no self-representing profile -> the entity's own name + the entity fields
//
// The middle case is why this renders no name input rather than a disabled one: an affordance whose
// save is guaranteed to be refused server-side is worse than no affordance.
function HeaderModalBody({ close }) {
  const context = useContext(ExpertContext);
  const { expert, isUpdating, generalError, viewerContext } = context;
  const { updateExpert } = useExpertUpdateApi({ ...context });

  // Display state for the three-way control, seeded from the entity's current value.
  // VisibilityControl only fires onChange from an actual click (never on mount), so
  // `setValue("visibility", ...)` below — and therefore the changed-fields diff — only ever sees
  // this key when the viewer actually touched it. Unlike PR, this writes onto the ENTITY (the
  // PromoEntity's own `visibility`, via qapi.experts.updateById), not the backing profile: the
  // profile-update policy doesn't cover a representing PR who lacks profile authority, while
  // PromoEntityPolicy::ALLOWLIST_ATTRS extends to anyone with entity authority — which is exactly
  // who this modal is already gated to (the pen/kebab that open it both require `editable`).
  const [visibility, setVisibility] = useState(expert.visibility);

  const selfProfileId = expert?.self_represented_profile_id ?? null;
  // profileEditable is a strict boolean from the server seed; `editable` is not (it arrives null for an
  // anonymous viewer), which is why only the former is compared against true.
  const canEditPersonName = Boolean(selfProfileId) && viewerContext?.profileEditable === true;
  const canEditEntityName = !selfProfileId;

  const {
    names,
    isLoading: isLoadingNames,
    error: namesError,
    applyCommittedNames,
  } = useExpertProfileNames(canEditPersonName ? selfProfileId : null);

  const resolver = useMemo(
    () => yupResolver(ExpertSchema({ personNameFields: canEditPersonName, entityNameField: canEditEntityName })),
    [canEditPersonName, canEditEntityName],
  );

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({ mode: "onBlur", resolver });

  const onSubmit = async (data, event) => {
    event.preventDefault();

    const original = {
      ...(canEditEntityName && { name: expert.name }),
      ...(canEditPersonName && { first_name: names?.first_name, last_name: names?.last_name }),
      description: canonicalizeText(expert.description),
      headline_custom: canonicalizeText(expert.headline_custom),
      visibility: expert.visibility,
      // Left raw, not `?? null`: an absent coordinate reads as undefined, so a Remove's null still
      // counts as a change and the whole bundle clears together.
      location_string: expert.location_string,
      location_latitude: expert.location_latitude,
      location_longitude: expert.location_longitude,
      location_country_code: expert.location_country_code,
    };
    const submitted = {
      ...(canEditEntityName && { name: data.name }),
      ...(canEditPersonName && { first_name: data.first_name, last_name: data.last_name }),
      description: canonicalizeText(data.description),
      headline_custom: canonicalizeText(data.headline_custom),
      // `visibility` was never `register`-ed, so react-hook-form only ever carries this key once
      // VisibilityControl's onChange has called setValue — an untouched control leaves it out of
      // `data` entirely, and therefore out of the changed-fields diff below (see the no-clobber
      // guard on the state above). LocationAutocomplete registers its keys the same way, so an
      // untouched location likewise sends nothing.
      ...(data.visibility !== undefined && { visibility: data.visibility }),
      ...(data.location_string !== undefined && {
        location_string: data.location_string,
        location_latitude: data.location_latitude,
        location_longitude: data.location_longitude,
        location_country_code: data.location_country_code,
      }),
    };

    // Only fields the form actually renders can appear here, so a variant without name inputs can never
    // send a name — the split below then routes each changed field to the record that owns it. Visibility
    // lands in entityAttributes here (unlike PR's profileAttributes split): it is a PromoEntity column.
    // eslint-disable-next-line camelcase
    const { first_name, last_name, ...entityAttributes } = getChangedData(original, submitted);
    // eslint-disable-next-line camelcase
    const profileAttributes = _.omitBy({ first_name, last_name }, _.isUndefined);

    // updateExpert closes the modal and silently reloads the page data on success.
    const outcome = await updateExpert({ profileId: selfProfileId, profileAttributes, entityAttributes });

    // The two-record save is not atomic: when the rename committed but the save still failed, the diff
    // baseline advances past the committed halves so retrying the remaining fields cannot resend them.
    if (!outcome.saved && outcome.profileCommitted) applyCommittedNames(profileAttributes);
  };

  // The name inputs prefill from a separate read, and react-hook-form takes uncontrolled defaults, so
  // the form cannot mount before that read settles. Only the self-representing + profile-authority form
  // waits; the other two have nothing to wait for.
  if (isLoadingNames) {
    return (
      <Modal.Body>
        <div className="text-muted">Loading...</div>
      </Modal.Body>
    );
  }

  // Without the current name there is nothing to diff a submitted name against, so the form would
  // either resubmit the person's name unnecessarily or blank it. Show the failure instead.
  if (namesError) {
    return (
      <>
        <Modal.Body>
          <ServerErrorAlert
            generalError={namesError}
            labelMap={HEADER_ERROR_LABELS}
            genericMessage="We couldn't load this profile's details. Please try again."
          />
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={close}>
            Close
          </button>
        </Modal.Footer>
      </>
    );
  }

  return (
    <>
      <Modal.Body>
        <ServerErrorAlert generalError={generalError} labelMap={HEADER_ERROR_LABELS} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            {canEditPersonName && (
              <>
                <div className="col-6">
                  <div className="row">
                    <label htmlFor="first_name" className="col-6 col-form-label">
                      First Name
                    </label>
                    <div className="col-6">
                      <input
                        id="first_name"
                        type="text"
                        className="form-control"
                        {...register("first_name")}
                        defaultValue={names?.first_name}
                      />
                      {errors?.first_name && <span className="small text-danger">{errors?.first_name?.message}</span>}
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="row">
                    <label htmlFor="last_name" className="col-6 col-form-label">
                      Last Name
                    </label>
                    <div className="col-6">
                      <input
                        id="last_name"
                        type="text"
                        className="form-control"
                        {...register("last_name")}
                        defaultValue={names?.last_name}
                      />
                      {errors?.last_name && <span className="small text-danger">{errors?.last_name?.message}</span>}
                    </div>
                  </div>
                </div>
              </>
            )}
            {canEditEntityName && (
              <div className="col-12">
                <div className="row">
                  <label htmlFor="name" className="col-12 col-form-label">
                    Name
                  </label>
                  <div className="col-12">
                    <input
                      id="name"
                      type="text"
                      className="form-control"
                      {...register("name")}
                      defaultValue={expert.name}
                    />
                    {errors?.name && <span className="small text-danger">{errors?.name?.message}</span>}
                  </div>
                </div>
              </div>
            )}
            <div className="col-12">
              <div className="row">
                <label htmlFor="headline_custom" className="col-12 col-form-label">
                  Headline
                </label>
                <div className="col-12">
                  <input
                    id="headline_custom"
                    type="text"
                    className="form-control"
                    {...register("headline_custom")}
                    defaultValue={expert.headline_custom}
                    // The derived headline showing today — so an owner with no custom headline can see
                    // what clearing this field falls back to (the featured job title).
                    placeholder={expert.headline}
                  />
                  {errors?.headline_custom && (
                    <span className="small text-danger">{errors?.headline_custom?.message}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="row">
                <label htmlFor="description" className="col-12 col-form-label">
                  About
                </label>
                <div className="col-12">
                  <textarea
                    id="description"
                    className="form-control"
                    {...register("description")}
                    defaultValue={expert.description}
                  />
                  {errors?.description && <span className="small text-danger">{errors?.description?.message}</span>}
                </div>
              </div>
            </div>
            <LocationField defaultValue={expert.location_string} setValue={setValue} error={errors?.location_string} />
            <div className="col-12">
              <div className="mt-3">
                <span className="col-form-label d-block">Profile Visibility</span>
                <VisibilityControl
                  value={visibility}
                  onChange={(newValue) => {
                    setVisibility(newValue);
                    // Registers the key with react-hook-form only on an actual click — see the
                    // comment by the `visibility` state above for why an untouched control must
                    // send no key.
                    setValue("visibility", newValue);
                  }}
                />
              </div>
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <SaveSpinnerButton
          inactiveText="Save Changes"
          activeText="Saving..."
          isSaving={isUpdating}
          className="btn btn-primary"
          onClick={handleSubmit(onSubmit)}
        />
        <button type="button" className="btn btn-secondary" onClick={close}>
          Close
        </button>
      </Modal.Footer>
    </>
  );
}

export default HeaderModalBody;
